import { useQueryParams } from 'hooks/useQueryParams';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { replaceUrl } from 'utils/helpers/replaceUrl';
import { getCurrencyPart, getSelectedCurrencyCode } from 'utils/store/currency';

export function useAmount() {
  const { i18n } = useTranslation();
  const { language } = i18n;
  const { amount } = useQueryParams();

  return {
    selectedAmount: validateAmount(getAmount(amount)),
    setAmount: useMemo(() => setFormattedAmount(language), [language]),
  };
}

function getAmount(param: string = '') {
  return isNaN(parseFloat(param)) && param !== '.' ? '' : param;
}

async function setAmount(amount?: string) {
  return await replaceUrl({ amount });
}

function setFormattedAmount(language: string) {
  return async function (amount?: string) {
    return await setAmount(validateAmountInput(language, amount));
  };
}

function keepDecimalAndDigits(decimal: string) {
  return new RegExp(`(?![\\d\\${decimal}]).`, 'g');
}

function restrictCurrencyLength(maxDigits: number) {
  return new RegExp(`(\\d{0,${maxDigits}}(?:\\.\\d{0,2})?)`);
}

export function validateAmountInput(language: string, amount?: string) {
  if (!amount) {
    return;
  }
  const decimal = getCurrencyPart('decimal', language)?.value ?? '.';
  return validateAmount(amount, decimal);
}

export function validateAmount(amount: string, decimal: string = '.') {
  const maxDigits = getSelectedCurrencyCode() === 'IDR' ? 12 : 8;
  const filteredAmount = amount
    .replace(keepDecimalAndDigits(decimal), '')
    .replace(decimal, '.')
    .match(restrictCurrencyLength(maxDigits))?.[0];
  return filteredAmount ?? '';
}
